import { AlertCircle, Download, LoaderCircle, ScrollText } from "lucide-react";
import { useEffect, useState } from "react";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  type EventoAuditoria,
  type FiltrosAuditoria,
  useAuditoria,
  useAuditoriaResumen,
} from "@/lib/auditoria";
import { descargarAuditoria, mensajeDeErrorExport } from "@/lib/export";
import { useObras } from "@/lib/obras";
import { useUsuarios } from "@/lib/usuarios";
import { AuditoriaFiltros } from "./AuditoriaFiltros";
import { EventoDetalle } from "./EventoDetalle";

// Retardo entre la última tecla y la consulta: evita disparar una consulta por pulsación
// mientras se teclea una IP o un id (la tabla puede ser enorme).
const RETARDO_FILTROS_MS = 300;

/** formatFecha da un instante ISO en formato local es-ES (día y hora). */
function formatFecha(iso: string): string {
  const fecha = new Date(iso);
  if (Number.isNaN(fecha.getTime())) {
    return "—";
  }
  return fecha.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** formatBytes presenta el tamaño de la tabla en MB (o KB si aún no llega a uno). */
function formatBytes(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) {
    return `${mb.toLocaleString("es-ES", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MB`;
  }
  return `${(bytes / 1024).toLocaleString("es-ES", { maximumFractionDigits: 0 })} KB`;
}

/** Tarjeta de una magnitud del resumen de volumen. */
function DatoVolumen({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-slate-500 text-sm">{etiqueta}</span>
      <span className="font-semibold text-slate-900">{valor}</span>
    </div>
  );
}

/**
 * AuditoriaPage es el visor del registro de auditoría (#254): filtros combinables, tabla
 * paginada por cursor keyset («Cargar más»), detalle del evento en modal y export xlsx. Es
 * ADMIN-only y GLOBAL —el registro cruza todas las obras (D1)—; el guard de ruta redirige a
 * la raíz a quien no sea admin y el backend es el guardián real.
 *
 * Las acciones se muestran TAL CUAL: la tabla es append-only y conserva acciones ya
 * retiradas del código (LOTO_EJECUTAR, EMAIL_CONFIG_SAVE…), que el visor no debe ocultar ni
 * traducir a una etiqueta inventada.
 */
export function AuditoriaPage() {
  // `borrador` es lo que hay en los campos; `filtros` es lo aplicado (con retardo).
  const [borrador, setBorrador] = useState<FiltrosAuditoria>({});
  const [filtros, setFiltros] = useState<FiltrosAuditoria>({});
  useEffect(() => {
    const t = setTimeout(() => setFiltros(borrador), RETARDO_FILTROS_MS);
    return () => clearTimeout(t);
  }, [borrador]);

  const consulta = useAuditoria(filtros);
  const resumen = useAuditoriaResumen();
  const usuarios = useUsuarios();
  const obras = useObras();

  const [seleccionado, setSeleccionado] = useState<EventoAuditoria | null>(null);
  const [exportando, setExportando] = useState(false);
  const [errorExport, setErrorExport] = useState<string | null>(null);

  const eventos = consulta.data?.pages.flatMap((p) => p.eventos) ?? [];

  async function exportar() {
    setExportando(true);
    setErrorExport(null);
    try {
      // Se exporta lo que hay en los campos, aunque el retardo de la consulta aún no haya
      // corrido: lo que el usuario ve es lo que se descarga.
      await descargarAuditoria(borrador);
    } catch (e) {
      // La cifra del tope la dice el SERVIDOR en su `message` (#355, D3): escribirla aquí
      // a mano era una copia que ya no sabía de qué ruta hablaba ni con qué unidad.
      setErrorExport(mensajeDeErrorExport(e));
    } finally {
      setExportando(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col">
          <h1 className="flex items-center gap-2 font-bold text-2xl text-slate-900 tracking-tight">
            <ScrollText className="h-6 w-6 text-primary-600" aria-hidden="true" />
            Auditoría
          </h1>
          <p className="text-base text-slate-500">
            Registro forense de la instancia: quién hizo qué, cuándo y desde dónde.
          </p>
        </div>
        <Button variant="outline" onClick={exportar} disabled={exportando}>
          <Download className="h-5 w-5" aria-hidden="true" />
          Exportar a Excel
        </Button>
      </div>

      {errorExport !== null ? (
        <Alert variant="destructive">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>{errorExport}</span>
        </Alert>
      ) : null}

      {resumen.data ? (
        <div className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:grid-cols-2 lg:grid-cols-4">
          <DatoVolumen
            etiqueta="Eventos (estimados)"
            valor={resumen.data.filas_estimadas.toLocaleString("es-ES")}
          />
          <DatoVolumen etiqueta="Tamaño en disco" valor={formatBytes(resumen.data.bytes)} />
          <DatoVolumen
            etiqueta="Primer evento"
            valor={resumen.data.primer_evento ? formatFecha(resumen.data.primer_evento) : "—"}
          />
          <DatoVolumen
            etiqueta="Último evento"
            valor={resumen.data.ultimo_evento ? formatFecha(resumen.data.ultimo_evento) : "—"}
          />
        </div>
      ) : null}

      <AuditoriaFiltros
        valor={borrador}
        onChange={setBorrador}
        usuarios={usuarios.data ?? []}
        obras={obras.data ?? []}
        resumen={resumen.data}
      />

      {consulta.isError ? (
        <Alert variant="destructive">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>No se pudo cargar el registro de auditoría. Inténtalo de nuevo.</span>
        </Alert>
      ) : null}

      {consulta.isPending ? (
        <div className="flex items-center justify-center gap-2 py-10 text-slate-500">
          <LoaderCircle className="h-6 w-6 animate-spin" aria-hidden="true" />
          Cargando eventos…
        </div>
      ) : null}

      {!consulta.isPending && !consulta.isError && eventos.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 border-dashed bg-white px-4 py-12 text-center text-slate-500">
          No hay eventos que casen con estos filtros.
        </div>
      ) : null}

      {eventos.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Fecha</TableHead>
              <TableHead>Usuario</TableHead>
              <TableHead>Acción</TableHead>
              <TableHead className="hidden md:table-cell">Tabla</TableHead>
              <TableHead className="hidden md:table-cell">Registro</TableHead>
              <TableHead className="hidden lg:table-cell">Obra</TableHead>
              <TableHead className="hidden lg:table-cell">IP</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {eventos.map((e) => (
              <TableRow
                key={e.id}
                className="cursor-pointer hover:bg-primary-50/60"
                onClick={() => setSeleccionado(e)}
              >
                <TableCell className="whitespace-nowrap">{formatFecha(e.created_at)}</TableCell>
                <TableCell>{e.nombre_usuario ?? "—"}</TableCell>
                <TableCell>
                  {/* Botón: además del clic en la fila, deja una vía accesible por teclado. */}
                  <button
                    type="button"
                    className="font-medium text-slate-900 underline-offset-2 hover:underline"
                    onClick={() => setSeleccionado(e)}
                  >
                    {e.accion}
                  </button>
                </TableCell>
                <TableCell className="hidden md:table-cell">{e.tabla}</TableCell>
                <TableCell className="hidden md:table-cell">{e.id_registro ?? "—"}</TableCell>
                <TableCell className="hidden lg:table-cell">{e.codigo_obra ?? "—"}</TableCell>
                <TableCell className="hidden lg:table-cell">{e.ip ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}

      {consulta.hasNextPage ? (
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={() => void consulta.fetchNextPage()}
            disabled={consulta.isFetchingNextPage}
          >
            {consulta.isFetchingNextPage ? "Cargando…" : "Cargar más"}
          </Button>
        </div>
      ) : null}

      <EventoDetalle evento={seleccionado} onClose={() => setSeleccionado(null)} />
    </div>
  );
}
